import React, { useCallback, useEffect, useState } from 'react';
import { Button } from "../bootstrap/index.jsx";
import { Adalicon, FaIcon } from "../common/Icons.jsx";
import DropdownMenu from '../common/DropdownMenu.jsx';
import { PortalDropdownMenu } from '../common/Portal.jsx';
import Tooltip from "../common/Tooltip.jsx";
import ImportWizardButton from './import_wizard/ImportWizardButton.jsx';
import { fetchJson } from '../../helpers/fetch.js';
import { ProjectQuickFilter } from './ProjectQuickFilter.jsx';
import {PresetManager} from './PresetManager.jsx';
import BulkActionsButton from './bulk_action/BulkActionsButton.jsx';
import BasketsDropdown from './match_thread/BasketsDropdown.jsx';
import PivotEditsDropdown from './pivot_edit/PivotEditsDropdown.jsx';

import {
  datagrid_export_rows_path,
  datagrid_edit_path,
  datagrid_show_path,
  datagrid_recalculate_formula_path
} from '~/routes';

const MODES = {
  view: 'view',
  edit: 'edit'
}

const MENU_ITEM_STYLE = { 
  display: 'flex',
  padding: '0.5rem 2rem 0.5rem 1.5rem',
  gap: '1rem',
};

// TODO: Move to generalized file/location
export const WithPermission = ({permissions, permission, children}) => {
  if (!permissions[permission]) {
    return null;
  }

  return children
}

const Toolbar = ({editMode, setEditMode, toggleStatusBarComp, gridRef, datagrid, context, hideTitle = false, addRowLabel = null, setDialogRow, ...rest}) => {
  const [viewChangeHistory, setViewChangeHistory] = useState(false);

  const onEditToggle = (enable) => {
    if (!context.is_readonly) {
      // Reload grid if aggregated to allow row editing
      if(gridRef.current.api.getValueColumns().length) {
        gridRef.current.api.setValueColumns([])
      }

      // Must be done in timeout, otherwise state gets updated mid-animation, and it breaks the grid.
      setTimeout(() => {setEditMode(enable)}, 0);
      toggleStatusBarComp(enable);
    }
  };

  const onViewChangeHistoryToggle = (enable) => {
    setViewChangeHistory(enable);

    gridRef.current.api.resetColumnState();
    gridRef.current.api.setFilterModel([]);
    gridRef.current.api.setGridOption('context', {...context, get_rows_params: {...(context.get_rows_params || {}), viewChangeHistory: enable}});
    gridRef.current.api.refreshServerSide();
  };

  const onChangeProjectContext = (project_id) => {
    let newContext = {...context, get_rows_params: {...(context.get_rows_params || {})}}
    newContext.get_rows_params.project_id = project_id
    gridRef.current.api.setGridOption('context', newContext);
    gridRef.current.api.refreshServerSide({purge: true});
  }

  const handleRecalculateFormula = (project_id) => {
    if (confirm(I18n.t('datagrid_recalculate_formula_confirm'))) {
      jQuery.post(datagrid_recalculate_formula_path({id: context.id, project_id: project_id}), (res) => {
        Turbo.visit(location.href, {action: 'replace'});
      });
    }
  }

  const hasFormula = datagrid.columns.some(col => col.formula && col.formula.trim() !== '');

  const bulkEditActions = <>
    <span style={{marginLeft: '2.25rem'}} className="bulk-action-arrow">↵</span>  
    <Button size="sm" type="danger" className="ml-1" onClick={rest.removeSelectedRows}>        
      <Adalicon type="trash" /> {I18n.t("delete")}
    </Button>
    <Button size="sm" className="ml-1" onClick={rest.addRow}>        
      <FaIcon type="plus" /> {(addRowLabel || I18n.t("add_row"))}
    </Button>
  </>

  const ModeDependent = useCallback(({children, mode}) => {
    if (mode == MODES.edit && !editMode) return null;
    if (mode == MODES.view && editMode) return null;

    return children

  }, [editMode])

  const exportAction = (type) => {
      if (gridRef.current.api){
        gridRef.current.api.setGridOption("loading", true);
        const params = {
          column_state: gridRef.current.api.getColumnState(),
          filter_model: gridRef.current.api.getFilterModel()
        }
        fetchJson(
          datagrid_export_rows_path({id: context.id, project_id: context.project_id, _options: true}),
            {
              method: 'POST',
              body: JSON.stringify({...params})
            }
        ).then(res => {
          //Turbo.visit(location.href);
          gridRef.current.api.setGridOption("loading", false);
          window.ToastNotification({message: I18n.t('datagrid_export_started_info'), horizontal: 'center', vertical: 'top', autoHideDuration: 5});
        }).catch(err => {
          gridRef.current.api.setGridOption("loading", false);
          console.error(err)
        })
      }
    
  }

  return  <div style={{padding: '1rem 0'}} className='datagrid-toolbar-component'>
        <ModeDependent mode={MODES.edit}>{bulkEditActions}</ModeDependent>
        <ModeDependent mode={MODES.view}>
          <div style={{display: 'grid', gap: '3rem', gridTemplateColumns: '1fr 1fr 1fr'}}>
            <div style={{display: 'flex', gap: '2rem', alignItems: 'center'}}>
              {!hideTitle && <h4 className='m-0 text-truncate' style={{maxWidth: '35vw'}}>{datagrid.name}</h4>}

              {datagrid.is_formula_calc_pending &&
                <Tooltip title={I18n.t('datagrid_pending_calculations')}>
                  <FaIcon type='flag'/>
                </Tooltip>
              }
            </div>

            <div style={{display: 'flex', gap: '1rem', justifySelf: 'auto', alignItems: 'center'}}>
              <WithPermission permissions={context.permissions} permission='switch_project_context'>
                <ProjectQuickFilter onChangeProjectContext={onChangeProjectContext}/>
              </WithPermission>

              {(context.programme_id == context.project_id || datagrid.presets.length > 0) &&
                <WithPermission permissions={context.permissions} permission='presets_view'>
                  <PresetManager datagrid={datagrid} context={context} gridRef={gridRef}/>
                </WithPermission>
              }
            </div>

            <div style={{display: 'flex', gap: '1rem', justifySelf: 'end', alignItems: 'center'}}>
              <WithPermission permissions={context.permissions} permission='show_match_threads'>
                <BasketsDropdown datagridId={datagrid.id} />
              </WithPermission>

              <WithPermission permissions={context.permissions} permission='show_pivot_datagrids'>
                <PivotEditsDropdown datagridId={datagrid.id} />
              </WithPermission>

              <WithPermission permissions={context.permissions} permission='export'>
                <DropdownMenu
                  menuItems={<>
                    <li style={MENU_ITEM_STYLE}>
                      <Button
                      size='md'
                      type="link"
                      style={{width: '100%', height: '100%', justifyContent: 'flex-start', gap: '1rem', color: 'black', padding: 0}}
                      onClick={()=> exportAction('csv')}
                      disabled
                      >
                        <span className="ag-icon ag-icon-csv"></span>
                        <div>{I18n.t("csv_export")}</div>
                      </Button>
                    </li>
                    <li role="separator" className="divider"></li>
                    <li style={MENU_ITEM_STYLE} onClick={()=> exportAction('excel')}>
                      <span className="ag-icon ag-icon-excel" role="presentation"></span>
                      {I18n.t("excel_export")}
                    </li>
                  </>}
                  menuProps={{rightToLeft: true}}
                >
                  <Button style={{display: 'flex'}}  type='default'>
                    <FaIcon type='upload'/>
                    {I18n.t("export")}
                  </Button>
                </DropdownMenu>
              </WithPermission>

              <WithPermission permissions={context.permissions} permission='import'>
                <ImportWizardButton {...{context, datagrid}} />
              </WithPermission>

              <WithPermission permissions={context.permissions} permission='edit'>
                <Tooltip title={I18n.t(context.is_readonly ? 'values_collected_using_reporting' : '')} options={{ placement: 'left' }}>
                  <Button onClick={() => onEditToggle(true)} disabled={context.is_readonly || viewChangeHistory}>
                    <FaIcon type='pencil' />
                    {I18n.t("edit")}
                  </Button>
                </Tooltip>
              </WithPermission>

              <WithPermission permissions={context.permissions} permission='single_row_add'>
                <Button onClick={() => setDialogRow({})} disabled={context.is_readonly || viewChangeHistory}>
                  <FaIcon type="plus" />
                  {(context?.singleRowAdd?.label || addRowLabel || I18n.t("add_row"))}
                </Button>
              </WithPermission>

              <PortalDropdownMenu
                buttonLabel={<FaIcon type='ellipsis-h'/>}
                size='md'
              >
                <WithPermission permissions={context.permissions} permission='view_change_history'>
                  <li style={MENU_ITEM_STYLE}>
                    <a onClick={() => onViewChangeHistoryToggle(!viewChangeHistory)} type={viewChangeHistory ? 'primary' : 'default'}>
                      <FaIcon type='history'/> {I18n.t(viewChangeHistory ? 'hide_change_history' : 'view_change_history')}
                    </a>
                  </li>
                </WithPermission>

                {hasFormula &&
                  <WithPermission permissions={context.permissions} permission='edit'>
                    <li style={MENU_ITEM_STYLE}>
                      <a onClick={() => handleRecalculateFormula()} disabled={datagrid.is_formula_calc_pending || viewChangeHistory}>
                        <FaIcon type='sync'/> {I18n.t("recalculate_formulas")}
                      </a>
                    </li>
                  </WithPermission>
                }

                <WithPermission permissions={context.permissions} permission='view_bulk_actions'>
                  <li style={MENU_ITEM_STYLE}>
                    <BulkActionsButton {...{gridRef, datagrid, project_id: context.project_id}}/>
                  </li>
                </WithPermission>

                {context.permissions.can_switch_to_configure_mode &&
                  <li style={MENU_ITEM_STYLE}>
                    <a href={datagrid_edit_path({id: context.id})}>
                      <FaIcon type='cog'/> {I18n.t('switch_to_configure_mode')}
                    </a>
                  </li>
                }

                {context.permissions.can_switch_to_view_mode &&
                  <li style={MENU_ITEM_STYLE}>
                    <a href={datagrid_show_path({id: context.id})}>
                      <FaIcon type='cog'/> {I18n.t('switch_to_view_mode')}
                    </a>
                  </li>
                }
              </PortalDropdownMenu>
            </div>
          </div>
        </ModeDependent>
      </div>
}

export default Toolbar;